<template>
	<div class="w-full h-full flex flex-col items-center">
		<ProfilesBar
			:profiles="profiles"
			:selected-profile="selectedProfile"
			@select-profile="SelectProfile"
		/>

		<div
			id="templates"
			class="w-full px-4 mb-2"
		>
			<Dropdown
				v-model="filters.templates"
				label="TEMPLATES"
			/>
		</div>

		<div
			id="priorities"
			class="w-full px-4 my-2"
		>
			<Dropdown
				v-model="filters.priorities"
				label="PRIORIDADE"
			/>
		</div>

		<div
			id="attendance-types"
			class="w-full px-4 my-2"
		>
			<Dropdown
				v-model="filters.attendanceTypes"
				label="ATENDIMENTO"
			/>
		</div>

		<SaveProfileModal
			v-if="showSaveProfileModal"
			@close="showSaveProfileModal = false"
			@save="SaveProfile"
		/>
	</div>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex'
	import {
		useCopyObject,
		useEachRecursively,
		useError,
		useConfirmation,
	} from '@/composables'

	export default {
		name: 'Filters',
		props: {
			showSidebar: {
				type: Boolean,
				default: true,
			},
		},
		data() {
			return {
				initializing: true,
				loadingFromProfile: false,
				profiles: {},
				selectedProfile: 'current_profile',
				lastSelectedProfile: null,
				saveFiltersDebounce: null,
				showSaveProfileModal: false,
				filters: {
					templates: {},
					attendanceTypes: {},
					priorities: {},
					showSidebar: true,
				},
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_object']),
			hasAtLeastOneTemplate() {
				if (this.filters.templates.checkedAll) return true
				if (!this.filters.templates?.children?.length > 0) return false

				let hasTemplate = false

				useEachRecursively(
					this.filters.templates?.children,
					(child, skipRef) => {
						if (child.checked) {
							hasTemplate = true
							skipRef.skip = true
							return
						}
					}
				)

				return hasTemplate
			},
			hasAtLeastOnePriority() {
				if (this.filters.priorities.checkedAll) return true
				if (!this.filters.priorities?.children?.length > 0) return false

				let hasPriority = false

				useEachRecursively(
					this.filters.priorities?.children,
					(child, skipRef) => {
						if (child.checked) {
							hasPriority = true
							skipRef.skip = true
							return
						}
					}
				)

				return hasPriority
			},
			hasAtLeastOneAttendanceType() {
				if (this.filters.attendanceTypes.checkedAll) return true
				if (!this.filters.attendanceTypes?.children?.length > 0)
					return false

				let hasAttendanceType = false

				useEachRecursively(
					this.filters.attendanceTypes?.children,
					(child, skipRef) => {
						if (child.checked) {
							hasAttendanceType = true
							skipRef.skip = true
							return
						}
					}
				)

				return hasAttendanceType
			},
			hasAtLeastOneFilter() {
				return (
					this.hasAtLeastOneTemplate ||
					this.hasAtLeastOnePriority ||
					this.hasAtLeastOneAttendanceType
				)
			},
		},
		watch: {
			hasAtLeastOneFilter: {
				handler(val) {
					this.$emit('hasAtLeastOneFilter', val)
				},
				immediate: true,
			},
			showSidebar: {
				handler(val) {
					this.filters.showSidebar = val

					this.SaveFiltersDebounce()
				},
				immediate: true,
			},
			filters: {
				handler() {
					if (!this.loadingFromProfile) {
						this.UnselectProfile()
						this.SetLastSelectedProfile()
						this.SaveFiltersDebounce()
					}

					this.Update()
				},
				deep: true,
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			async GetProfiles() {
				this.StartLoading()

				return window.api
					.call('post', '/reception/get-profiles', {
						workgroup_id: this.requester_selected_object.id,
					})
					.then(async ({ status, data }) => {
						if (status !== 200) {
							return useError('Erro ao obter os filtros!')
						}

						this.profiles = { ...data.profiles }
						this.profiles = Object.fromEntries(
							Object.entries(this.profiles).map(
								([key, profile]) => {
									this.OrderProfileAttendanceTypes(profile)
									return [key, profile]
								}
							)
						)

						await this.$nextTick()

						this.lastSelectedProfile = data.lastSelectedProfile

						this.SelectProfile(
							this.lastSelectedProfile ?? 'current_profile',
							true
						)
					})
					.finally(() => {
						this.initializing = false

						this.FinishLoading()
					})
			},
			OrderProfileAttendanceTypes(profile) {
				profile.filters.attendanceTypes.children.sort((a, b) => {
					if (a.label < b.label) return -1
					if (a.label > b.label) return 1
					return 0
				})
			},
			async SelectProfile(profileName, bypass = false) {
				let profile = this.profiles[profileName]
				if (!profile) return

				this.loadingFromProfile = true

				const isToggle = this.selectedProfile === profileName
				if (isToggle) {
					this.selectedProfile = 'current_profile'
					profile = this.profiles.current_profile
				} else {
					this.selectedProfile = profileName
				}

				await this.LoadProfileFilters(useCopyObject(profile))
				await this.SetLastSelectedProfile(this.selectedProfile)

				if (!bypass) {
					this.Update(false)
					this.ApplyFilters()
				}

				this.loadingFromProfile = false
			},
			async LoadProfileFilters(profile) {
				this.filters = {
					templates: useCopyObject(profile.filters.templates),
					priorities: useCopyObject(profile.filters.priorities),
					attendanceTypes: useCopyObject(
						profile.filters.attendanceTypes
					),
					showSidebar: profile.filters.showSidebar,
				}
			},
			async SetLastSelectedProfile(profile = 'current_profile') {
				if (this.initializing) return
				if (this.lastSelectedProfile === profile) return

				return window.api
					.call('post', '/filters/set-last-selected-profile', {
						workgroup_id: this.requester_selected_object.id,
						module: 'reception',
						profile,
					})
					.then(async ({ status }) => {
						if (status !== 200) {
							return useError(
								'Erro ao memorizar o último perfil selecionado!'
							)
						}

						this.lastSelectedProfile = profile
					})
			},
			Update(autoSave = true) {
				this.$emit('change', {
					filters: useCopyObject(this.filters),
					initializing: this.initializing,
				})

				if (autoSave) {
					this.SaveFiltersDebounce(this.selectedProfile)
				}
			},
			ApplyFilters() {
				this.$emit('apply', { filters: useCopyObject(this.filters) })
			},
			ClearFilters() {
				this.UnselectProfile()

				useEachRecursively(this.filters.templates?.children, child => {
					child.checked = false
				})

				useEachRecursively(this.filters.priorities?.children, child => {
					child.checked = false
				})

				useEachRecursively(
					this.filters.attendanceTypes?.children,
					child => {
						child.checked = false
					}
				)

				this.SaveFiltersDebounce()
			},
			async UnselectProfile() {
				this.selectedProfile = 'current_profile'
			},
			SaveFiltersDebounce(profile = 'current_profile') {
				if (this.saveFiltersDebounce)
					clearTimeout(this.saveFiltersDebounce)

				this.saveFiltersDebounce = setTimeout(() => {
					this.SaveFilters(profile)
				}, 1000)
			},
			async SaveFilters(
				profile = 'current_profile',
				withConfirmation = false
			) {
				return window.api
					.call('post', '/filters/save-filters', {
						workgroup_id: this.requester_selected_object.id,
						module: 'reception',
						filters: this.filters,
						profile,
					})
					.then(async ({ status }) => {
						if (status !== 200) {
							return useError('Erro ao salvar os filtros!')
						}

						if (withConfirmation) useConfirmation()

						const profileToUpdate = this.profiles[profile]

						if (!profileToUpdate) {
							this.GetProfiles()
						} else {
							profileToUpdate.filters = useCopyObject(
								this.filters
							)

							this.lastSelectedProfile = profile
						}
					})
			},
			OpenSaveProfilesModal() {
				this.showSaveProfileModal = true
			},
			async SaveProfile(profile = 'current_profile') {
				await this.SaveFilters(profile, true)
				await this.UnselectProfile()
				await this.SelectProfile(profile, true)
			},
		},
		beforeMount() {
			if (this.initializing) {
				this.GetProfiles()
			}
		},
	}
</script>

<style lang="scss" scoped></style>
